import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthProvider";
import { addNotice, getActiveNotices, deleteNotice, Notice } from "../../services/notice.service";
import { Button } from "../common/Button";
import { toast } from "react-hot-toast";

export const NoticeManager: React.FC = () => {
  const { user } = useAuth();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [form, setForm] = useState({ title: "", message: "" });

  const fetchNotices = async () => {
    const data = await getActiveNotices();
    setNotices(data);
  };

  useEffect(() => { fetchNotices(); }, []);

  const handlePost = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!form.title || !form.message) return toast.error("Fill all fields");

    setLoading(true);
    try {
      await addNotice({
        type: user.roles.includes('admin') ? 'admin' : 'chef',
        title: form.title,
        message: form.message,
        authorName: user.name || "Staff",
        authorId: user.uid
      });
      toast.success("Notice Posted!");
      setForm({ title: "", message: "" });
      fetchNotices(); // Refresh list
    } catch (error) {
      toast.error("Failed to post notice");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if(!window.confirm("Delete this notice?")) return;
    await deleteNotice(id);
    fetchNotices();
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
      <h3 className="text-lg font-bold text-gray-800 mb-4">📢 Manage Notice Board</h3>
      
      {/* POST FORM */}
      <form onSubmit={handlePost} className="space-y-3 mb-6 p-4 bg-gray-50 rounded-lg">
        <div>
          <input 
            className="w-full p-2 border rounded text-sm font-bold"
            placeholder="Title (e.g. Special Offer)"
            value={form.title}
            onChange={e => setForm({...form, title: e.target.value})}
          />
        </div>
        <div>
          <textarea 
            className="w-full p-2 border rounded text-sm"
            placeholder="Message details..."
            rows={2}
            value={form.message}
            onChange={e => setForm({...form, message: e.target.value})}
          />
        </div>
        <div className="flex justify-end">
           <Button isLoading={loading} className="text-xs px-4 py-2">Post Notice</Button>
        </div>
      </form>

      {/* ACTIVE NOTICES LIST */}
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {notices.length === 0 && <p className="text-xs text-gray-400 text-center">No active notices.</p>}
        {notices.map(notice => (
          <div key={notice.id} className="flex justify-between items-start p-3 border rounded-lg hover:bg-gray-50">
             <div>
                <div className="flex items-center gap-2">
                   <span className={`text-[10px] px-1.5 rounded uppercase font-bold ${notice.type === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>{notice.type}</span>
                   <span className="text-xs font-bold text-gray-800">{notice.title}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">{notice.message}</p>
                <div className="text-[10px] text-gray-400 mt-1">By {notice.authorName}</div>
             </div>
             {(user?.roles.includes('admin') || user?.uid === notice.authorId) && (
                 <button onClick={() => handleDelete(notice.id!)} className="text-red-500 hover:text-red-700 text-xs font-bold">Delete</button>
             )}
          </div>
        ))}
      </div>
    </div>
  );
};